import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { map, Observable, of, take, tap } from 'rxjs';
import { ChargePlan, ChargePlanPurchase, Currency, PaywallSettings, Product, SimulatedPayment } from '../models/paywall-settings';
import { isQuantityChargePlan } from '../helpers/quantity.helper';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG } from '../../environments/environment.injection.token';
import packageJson from '../../../package.json';

@Injectable({
  providedIn: 'root'
})
export class PaywallService {
  private readonly appConfig = inject(APP_CONFIG);
  http: HttpClient = inject(HttpClient);

  paywallId = signal<string>('');
  legalEntityId = signal<string>('');
  tenantId = signal<string>('');
  private readonly apiUrlOverride = signal('');
  readonly isPreview = signal(false);
  /**
   * Issued by Younium to the paywall editor's preview. A test mode checkout creates a real order and invoice without
   * a charge, so the API only accepts test mode purchases with this token.
   */
  readonly previewToken = signal('');
  /** Why the checkout cannot be bought from here, or '' when it can. */
  readonly purchaseBlockedReason = computed(() => {
    const testMode = !!this.settings()?.testMode;
    if (testMode && this.previewToken()) {
      return '';
    }
    if (this.isPreview()) {
      return 'Purchases are disabled in preview.';
    }
    if (testMode) {
      return 'This paywall is in test mode and cannot be bought yet.';
    }
    return '';
  });

  readonly baseUrl = computed(() => {
    const override = this.normalizeBaseUrl(this.apiUrlOverride());
    return override || this.normalizeBaseUrl(this.appConfig.dataUrl);
  });

  setApiUrl(url?: string): void {
    this.apiUrlOverride.set(url ?? '');
  }

  public step: WritableSignal<'cards' | 'checkout' | 'simulate' | 'success' | 'contact' | 'contactSuccess'> = signal('cards');
  /** Set when a test mode checkout is ready; shown on the 'simulate' step in place of the Stripe checkout. */
  public simulatedPayment = signal<SimulatedPayment | undefined>(undefined);
  selectedBillingPeriod = signal({
    label: 'Monthly', value: 0
  });

  public selectedCurrency = signal<Currency | any>({});
  public countries: WritableSignal<any[]> = signal([]);
  public settings: WritableSignal<PaywallSettings | undefined> = signal(undefined);
  public accountFields: WritableSignal<any[]> = signal([]);
  public orderFields: WritableSignal<any[]> = signal([]);
  /** The card the buyer picked: one charge plan with all of its charges. */
  public selectedChargePlan = signal<ChargePlan | undefined>(undefined);
  /** Seat count for the selected plan's quantity based charges. */
  public selectedQuantity = signal<number>(1);
  /** Seats chosen on each card, by charge plan id. Kept here so they survive the trip to checkout and Back. */
  public cardQuantities = signal<Record<string, number>>({});
  /** What the buyer typed in checkout, kept in memory only so Back and Select again do not clear the form. */
  public checkoutDraft: Record<string, unknown> | undefined;
  public contactEmail = signal<string>('');
  /** Before the key was scoped to the paywall, so any paywall in the tab restored another one's choice. */
  private readonly legacySelectionStorageKey = 'paywallSelection';
  /** One saved selection per paywall: a choice made on one paywall must not preselect another in the same tab. */
  private selectionStorageKey(): string {
    return `paywallSelection:${this.paywallId()}`;
  }

  allProducts(): Product[] {
    const settings = this.settings();
    if (!settings) {
      return [];
    }
    if (settings.products?.length) {
      return settings.products;
    }
    return settings.product ? [settings.product] : [];
  }

  allChargePlans(): ChargePlan[] {
    return this.allProducts().flatMap(product => product.chargePlans ?? []);
  }

  productForPlan(plan: ChargePlan | undefined): Product | undefined {
    if (!plan) {
      return this.allProducts()[0];
    }
    return this.allProducts().find(product => product.chargePlans?.some(item => item.id === plan.id))
      ?? this.allProducts()[0];
  }

  productForSelection(): Product | undefined {
    return this.productForPlan(this.selectedChargePlan());
  }

  quantityFor(plan: ChargePlan | undefined): number {
    if (plan && !isQuantityChargePlan(plan)) {
      return 1;
    }
    return this.selectedQuantity() || 1;
  }

  setSelection(plan: ChargePlan | undefined, quantity: number): void {
    this.selectedChargePlan.set(plan);
    this.selectedQuantity.set(plan && isQuantityChargePlan(plan) && quantity > 0 ? quantity : 1);
    if (plan && isQuantityChargePlan(plan)) {
      this.cardQuantities.update(current => ({ ...current, [plan.id]: this.selectedQuantity() }));
    }
  }

  purchaseChargePlans(): ChargePlanPurchase[] {
    const plan = this.selectedChargePlan();
    return plan ? [{ chargePlanId: plan.id, quantity: this.quantityFor(plan) }] : [];
  }

  getCurrencies(): Observable<Currency[]> {
    return this.http.get<Currency[]>(`${this.baseUrl()}/paywall/currencies`)
  }

  getCountries(paywallId: string, legalEntityId: string, tenantId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl()}/countries`, {
      headers: {
          'PaywallId': paywallId,
          'LegalEntityId': legalEntityId,
          'TenantId': tenantId,
          'X-Api-Version': packageJson.version
        }
    }).pipe(
      tap((countries: any[]) => {
        this.countries.set(countries);
      })
    );
  }

  getSettings(paywallId: string, legalEntityId: string, tenantId: string): Observable<PaywallSettings> {
    if(paywallId && legalEntityId && tenantId) {

      this.paywallId.set(paywallId);
      this.legalEntityId.set(legalEntityId);
      this.tenantId.set(tenantId);

      return this.http.get<PaywallSettings>(`${this.baseUrl()}/paywalls/${paywallId}`, {
        headers: {
          'PaywallId': paywallId,
          'LegalEntityId': legalEntityId,
          'TenantId': tenantId,
          'X-Api-Version': packageJson.version
        }
      }).pipe(
        tap((settings: any) => {
          const paywall = settings.paywall;
          if (paywall && !paywall.products?.length && paywall.product) {
            paywall.products = [paywall.product];
          }
          this.settings.set(paywall);
          this.accountFields.set(settings.accountFields || []);
          this.orderFields.set(settings.orderFields || []);
          this.restoreSelection();
        })
      );
    }

    return this.http.get(`${this.baseUrl()}/paywall1.json`).pipe(
      map((response: any) => {
        return {
          billingPeriods: [0, 2],
          currencies: [
            { id: 'SEK', description: 'Swedish Krona', code: 'SEK', currencySymbol: 'kr' },
            { id: 'USD', description: 'US Dollar', code: 'USD', currencySymbol: '$' },
            // { id: 'EUR', description: 'Euro', code: 'EUR' }
          ],
          style: {
            baseColor: '#000',
            accentColor: '#fff',
            backgroundColor: '#ccc',
            lightGreyColor: '',
            darkGreyColor: '',
            primaryFont: '',
            baseFontColor: '',
            borderRadius: '',
            boxShadow: ''
          },
          product: response
        } as PaywallSettings;
      }),
      tap(console.log),
      tap((s) => this.settings.set(s))
    );
  }

  persistSelection(): void {
    // The editor's preview shares the admin tab's sessionStorage with every other paywall's preview and only lives
    // in memory, so each preview starts from the defaults; Back keeps the seats through cardQuantities.
    if (this.isPreview()) {
      return;
    }
    const chargePlans = this.purchaseChargePlans();
    sessionStorage.setItem(this.selectionStorageKey(), JSON.stringify({
      chargePlans,
      chargePlanId: this.selectedChargePlan()?.id,
      quantity: this.selectedQuantity(),
      billingPeriod: this.selectedBillingPeriod(),
      currency: this.selectedCurrency()
    }));
  }

  restoreSelection(): void {
    if (this.isPreview()) {
      return;
    }
    sessionStorage.removeItem(this.legacySelectionStorageKey);
    const key = this.selectionStorageKey();
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return;
    }

    try {
      const saved = JSON.parse(raw);
      // Older versions stored several plans for a bundled product card; a card is now a single plan.
      const savedPlan: { chargePlanId?: string; id?: string; quantity?: number } | undefined = saved.chargePlans?.length
        ? saved.chargePlans[0]
        : (saved.chargePlanId ? { chargePlanId: saved.chargePlanId, quantity: saved.quantity } : undefined);
      const restoredPlan = savedPlan
        ? this.allChargePlans().find(plan => plan.id === (savedPlan.chargePlanId ?? savedPlan.id))
        : undefined;
      if (restoredPlan) {
        this.setSelection(restoredPlan, savedPlan?.quantity ?? 1);
      }
      if (saved.billingPeriod) {
        this.selectedBillingPeriod.set(saved.billingPeriod);
      }
      // The page reloads after the Stripe redirect; keep the buyer's currency rather than the cards view's default.
      if (saved.currency?.code) {
        this.selectedCurrency.set(saved.currency);
      }
      // The purchase is done: the Thank you screen has what it needs, and the next visit starts from the defaults.
      if (this.step() === 'success') {
        sessionStorage.removeItem(key);
      }
    } catch {
      sessionStorage.removeItem(key);
    }
  }

  constructor() { }

  private normalizeBaseUrl(url: string): string {
    return (url ?? '').trim().replace(/\/+$/, '');
  }
}
